import { Accordion } from '@/src/design-system/accordion'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks'
import type {
  MakePaymentComponentType,
  PaymentCardStackScreenProps,
} from '@/src/experiences/card-payments/screens/MakePayment'
import { SchedulePaymentsApi } from '@/src/experiences/card-payments/store/api'
import { useMemo } from 'react'
import { Text } from 'react-native'
import {
  ScheduledPaymentEntryPoint,
  styles as scheduledPaymentEntryPointStyles,
} from '@/src/experiences/card-payments/components/ScheduledPaymentEntryPoint'
import { PaymentIcon } from '@/src/assets/icons/PaymentIcon'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import {
  PaymentScheduleRow,
  renderPaymentScheduleAmount,
} from '@/src/experiences/card-activity/components/PaymentScheduleRow'
import { formatToTextMonth } from '@/src/common/utils/date'
import { AutoPayIcon } from '@/src/assets/icons/AutoPayIcon'
import { CardPaymentsRoutes } from '@/src/experiences/card-payments/navigation/routes'
import { mesaColorNames } from '@/src/common/theme'
import { formatCentsAsDollars } from '@/src/common/utils/general'

function useCardAccountId() {
  const cardAccountQuery = useCardAccount()
  return cardAccountQuery.data?.[0]?.id
}

export function PlatformMakePayment({
  MakePaymentComponent,
  ...props
}: {
  MakePaymentComponent: MakePaymentComponentType
} & PaymentCardStackScreenProps) {
  const cardAccountId = useCardAccountId()

  return cardAccountId ? (
    <PlatformMakePaymentWithSchedules
      {...props}
      cardAccountId={cardAccountId}
      MakePaymentComponent={MakePaymentComponent}
    />
  ) : (
    <MakePaymentComponent {...props} loading />
  )
}

function PlatformMakePaymentWithSchedules({
  MakePaymentComponent,
  cardAccountId,
  ...props
}: {
  cardAccountId: NonNullable<ReturnType<typeof useCardAccountId>>
  MakePaymentComponent: MakePaymentComponentType
} & PaymentCardStackScreenProps) {
  const query = SchedulePaymentsApi.endpoints[
    'GET /card/:cardAccountId/payments/schedules'
  ].useQuery({ params: { cardAccountId } })

  const schedules = useMemo(
    () => (query.data ? Object.groupBy(query.data, (schedule) => schedule.type) : undefined),
    [query.data],
  )

  const recurringPayment = schedules?.recurring?.[0]

  return (
    <MakePaymentComponent
      {...props}
      autoPayEntryPoint={
        <props.CardStackLink
          href={
            recurringPayment
              ? CardPaymentsRoutes.CardManagementAutoPayReview.route
              : CardPaymentsRoutes.CardManagementAutoPay.route
          }
          testID={CardPaymentsConstants.Ids.Press.ScheduleAutoPay}
          asChild
        >
          <ScheduledPaymentEntryPoint
            Icon={AutoPayIcon}
            style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 16, paddingBottom: 16 }}
          >
            {recurringPayment ? (
              <Text style={scheduledPaymentEntryPointStyles.title}>
                Auto-pay scheduled for{' '}
                {(() => {
                  switch (recurringPayment.amountType) {
                    case 'min':
                      return 'Minimum Payment'
                    case 'statement':
                      return 'Remaining Statement Balance'
                    case 'total':
                      return 'Current Balance'
                    case 'static':
                      return formatCentsAsDollars(recurringPayment.amountCents)
                    default:
                      recurringPayment satisfies never
                      return ''
                  }
                })()}{' '}
                on {formatToTextMonth(new Date(recurringPayment.processDateTime), {})}
              </Text>
            ) : (
              <Text
                style={[
                  scheduledPaymentEntryPointStyles.title,
                  { color: mesaColorNames['Greyscale/500 #6E7375'] },
                ]}
              >
                {CardPaymentsConstants.Strings.AutoPayOff}
              </Text>
            )}
          </ScheduledPaymentEntryPoint>
        </props.CardStackLink>
      }
      oneTimeScheduledTransfers={
        schedules?.single?.[0] ? (
          schedules.single[1] ? (
            <Accordion
              triggerProps={{
                style: { paddingLeft: 16, paddingRight: 16, paddingTop: 16, paddingBottom: 16 },
              }}
              trigger={
                <ScheduledPaymentEntryPoint
                  Icon={PaymentIcon}
                  chevronWrapperProps={{ display: 'none' }}
                >
                  <Text
                    style={scheduledPaymentEntryPointStyles.title}
                    testID={CardPaymentsConstants.Ids.Text.MultipleOneTimePaymentsScheduled}
                  >
                    Multiple One-time Payments scheduled
                  </Text>
                </ScheduledPaymentEntryPoint>
              }
            >
              {schedules.single.map((oneTimeScheduledTransfer, index) => (
                <PaymentScheduleRow
                  key={oneTimeScheduledTransfer.id}
                  {...oneTimeScheduledTransfer}
                  index={index}
                  linkProps={{
                    As: props.CardStackLink,
                    href: {
                      pathname: '/main/card/card-management/payment/oneTimePayment/[id]',
                      params: { id: oneTimeScheduledTransfer.id },
                    },
                  }}
                />
              ))}
            </Accordion>
          ) : (
            <props.CardStackLink
              href={{
                pathname: '/main/card/card-management/payment/oneTimePayment/[id]',
                params: { id: schedules.single[0].id },
              }}
              asChild
            >
              <ScheduledPaymentEntryPoint
                testID={CardPaymentsConstants.Ids.Text.SingleOneTimePaymentScheduled}
                Icon={PaymentIcon}
                style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 16, paddingBottom: 16 }}
              >
                <Text style={scheduledPaymentEntryPointStyles.title}>
                  One-time Payment scheduled for {renderPaymentScheduleAmount(schedules.single[0])}
                  {schedules.single[0].processDateTime
                    ? ` on ${formatToTextMonth(new Date(schedules.single[0].processDateTime), {})}`
                    : ''}
                </Text>
              </ScheduledPaymentEntryPoint>
            </props.CardStackLink>
          )
        ) : null
      }
    />
  )
}
